import React, { useState, useEffect, useMemo } from 'react';
import {
  FileText, AlertTriangle, Shield, MessageSquare, ClipboardList,
  Plus, X, CheckCircle2, Check, Cloud, CloudOff, Loader2,
} from 'lucide-react';

function ListField({ label, items, onChange, placeholder }) {
  const [input, setInput] = useState("");

  function add() {
    const val = input.trim();
    if (!val) return;
    onChange([...items, val]);
    setInput("");
  }

  function remove(idx) {
    onChange(items.filter((_, i) => i !== idx));
  }

  return (
    <div className="gb-list-field">
      <label>{label}</label>
      <div className="gb-list-input-row">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          data-testid={`list-input-${label.toLowerCase().replace(/\s+/g, "-")}`}
        />
        <button type="button" onClick={add} className="gb-list-add" data-testid={`list-add-${label.toLowerCase().replace(/\s+/g, "-")}`}>
          <Plus size={14} />
        </button>
      </div>
      {items.length > 0 && (
        <ul className="gb-list-items">
          {items.map((item, i) => (
            <li key={i}>
              <span>{item}</span>
              <button type="button" onClick={() => remove(i)} className="gb-list-rm"><X size={12} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ─── Text field ─── */
function Field({ label, name, value, onChange, placeholder, multiline }) {
  const Tag = multiline ? "textarea" : "input";
  return (
    <div className="gb-field">
      <label>{label}</label>
      <Tag
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={multiline ? 4 : undefined}
        data-testid={`field-${name}`}
      />
    </div>
  );
}

/* ─── Step bar ─── */
const STEPS = [
  { id: 1, label: "Case Info",  icon: FileText,        fields: ["grievance_title", "employee_name", "incident_date"] },
  { id: 2, label: "Narrative",  icon: MessageSquare,   fields: ["issue_summary", "what_happened"] },
  { id: 3, label: "Analysis",   icon: AlertTriangle,   fields: ["possible_issue_areas", "supporting_facts"] },
  { id: 4, label: "Prep",       icon: Shield,          fields: ["steward_talking_points"] },
  { id: 5, label: "Review",     icon: ClipboardList,   fields: [] },
];

function _hasValue(v) {
  if (v == null) return false;
  if (Array.isArray(v)) return v.length > 0;
  return String(v).trim().length > 0;
}

function computeStepProgress(step, form) {
  if (!step.fields.length) return 1;
  const filled = step.fields.filter((f) => _hasValue(form[f])).length;
  return filled / step.fields.length;
}

function StepBar({ current, form, onJump }) {
  return (
    <div className="gb-step-bar" data-testid="gb-step-bar">
      {STEPS.map((s) => {
        const pct = computeStepProgress(s, form);
        const done = pct >= 1;
        const started = pct > 0;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onJump?.(s.id)}
            className={`gb-step ${current >= s.id ? "active" : ""} ${current === s.id ? "current" : ""} ${done ? "done" : ""}`}
            data-testid={`gb-step-${s.id}`}
            aria-current={current === s.id ? "step" : undefined}
            aria-label={`Step ${s.id} ${s.label} ${Math.round(pct * 100)}% complete`}
          >
            <span className="gb-step-num">
              {done ? <Check size={12} strokeWidth={3} /> : s.id}
            </span>
            <span className="gb-step-label">{s.label}</span>
            <span
              className="gb-step-progress"
              style={{ transform: `scaleX(${started ? pct : 0})` }}
              aria-hidden="true"
            />
          </button>
        );
      })}
    </div>
  );
}

/* Overall progress across all fields required per step. */
function computeOverallProgress(form) {
  const required = STEPS.flatMap((s) => s.fields);
  if (!required.length) return 1;
  const filled = required.filter((f) => _hasValue(form[f])).length;
  return filled / required.length;
}

/* ─── Status badge ─── */
function StatusBadge({ status, onStatusChange }) {
  const statuses = [
    { value: "draft", label: "Draft", color: "var(--text-muted)" },
    { value: "needs_review", label: "Needs Review", color: "#f59e0b" },
    { value: "ready_for_steward", label: "Ready for Steward", color: "#22c55e" },
  ];
  return (
    <div className="gb-status-row" data-testid="status-selector">
      {statuses.map((s) => (
        <button
          key={s.value}
          type="button"
          className={`gb-status-btn ${status === s.value ? "selected" : ""}`}
          style={{ "--btn-accent": s.color }}
          onClick={() => onStatusChange(s.value)}
          data-testid={`status-${s.value}`}
        >
          {status === s.value && <CheckCircle2 size={13} />}
          {s.label}
        </button>
      ))}
    </div>
  );
}

/* ─── Main page ─── */
/* ─── Save state indicator (Cloud icon + timestamp) ─── */
function SaveStateIndicator({ state, lastSavedAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);

  const rel = useMemo(() => {
    if (!lastSavedAt) return "";
    const diffSec = Math.max(1, Math.round((now - lastSavedAt.getTime()) / 1000));
    if (diffSec < 60)   return `${diffSec}s ago`;
    const m = Math.round(diffSec / 60);
    if (m < 60)         return `${m}m ago`;
    const h = Math.round(m / 60);
    return `${h}h ago`;
  }, [now, lastSavedAt]);

  const cfg = {
    idle:   { label: "Not saved yet",       Icon: Cloud,   className: "gb-save-idle"   },
    dirty:  { label: "Unsaved changes",     Icon: Cloud,   className: "gb-save-dirty"  },
    saving: { label: "Saving…",             Icon: Loader2, className: "gb-save-saving" },
    saved:  { label: `Saved · ${rel}`,      Icon: Check,   className: "gb-save-saved"  },
    error:  { label: "Save failed",         Icon: CloudOff,className: "gb-save-error"  },
  }[state] || { label: "", Icon: Cloud, className: "" };

  return (
    <div className={`gb-save-state ${cfg.className}`} data-testid="gb-save-state" data-state={state}>
      <cfg.Icon size={14} className={state === "saving" ? "gb-spin" : ""} />
      <span>{cfg.label}</span>
    </div>
  );
}

export {
  ListField, Field, StepBar, StatusBadge, SaveStateIndicator,
  STEPS, computeStepProgress, computeOverallProgress,
};
